import { Component, type ErrorInfo, type ReactNode, useEffect, useState } from "react";
import { api } from "./api";
import type { ComplianceSummary } from "./types";
import KpiCards from "./components/KpiCards";
import ThreePillars from "./components/ThreePillars";
import MonthEndChart from "./components/MonthEndChart";
import RepeatOffenders from "./components/RepeatOffenders";
import Heatmap from "./components/Heatmap";
import TeamOrgChart from "./components/TeamOrgChart";
import DepartmentTiles from "./components/DepartmentTiles";
import AboutPage from "./components/AboutPage";
import { signOut, userDisplayName, userEmail } from "./auth/keycloak";

class SectionBoundary extends Component<
  { label: string; children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[${this.props.label}] render error`, error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <section className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800 shadow-card">
          <strong className="font-semibold">{this.props.label} failed to render.</strong>
          <div className="mt-1 font-mono text-xs">
            {String(this.state.error.message || this.state.error)}
          </div>
        </section>
      );
    }
    return this.props.children;
  }
}

type Status =
  | { kind: "loading" }
  | { kind: "ready"; data: ComplianceSummary }
  | { kind: "error"; message: string };

type Route = "dashboard" | "about";

function readRoute(): Route {
  return window.location.hash === "#about" ? "about" : "dashboard";
}

function RanchoLogo() {
  return (
    <img
      src="/rancho-logo.png"
      alt="Rancho BioSciences"
      className="h-10 w-auto"
    />
  );
}

function RefreshButton({
  onClick,
  refreshing,
  disabled,
}: {
  onClick: () => void;
  refreshing: boolean;
  disabled: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || refreshing}
      className="btn-rancho text-sm"
    >
      {refreshing ? "Refreshing" : "Refresh"}
      <span className="arrow">{refreshing ? "..." : "→"}</span>
    </button>
  );
}

export default function App() {
  const [route, setRoute] = useState<Route>(readRoute());
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const onHash = () => setRoute(readRoute());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  async function load() {
    setStatus({ kind: "loading" });
    try {
      const data = await api.getSummary();
      setStatus({ kind: "ready", data });
    } catch (e) {
      setStatus({ kind: "error", message: (e as Error).message });
    }
  }

  async function refresh() {
    setRefreshing(true);
    try {
      const data = await api.refresh();
      setStatus({ kind: "ready", data });
    } catch (e) {
      setStatus({ kind: "error", message: (e as Error).message });
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    if (route === "dashboard") load();
  }, [route]);

  return (
    <div className="min-h-screen flex flex-col bg-rancho-bg-soft">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.location.hash = "";
            }}
            aria-label="Rancho BioSciences home"
          >
            <RanchoLogo />
          </a>
          <div className="flex items-center gap-4">
            <nav className="flex items-center gap-1 text-sm">
              <NavLink href="#" active={route === "dashboard"}>Dashboard</NavLink>
              <NavLink href="#about" active={route === "about"}>About compliance</NavLink>
            </nav>
            {route === "dashboard" && status.kind === "ready" && (
              <span className="text-xs text-rancho-slate hidden xl:inline">
                Built {new Date(status.data.built_at).toLocaleString()}
              </span>
            )}
            {route === "dashboard" && (
              <RefreshButton
                onClick={refresh}
                refreshing={refreshing}
                disabled={status.kind === "loading"}
              />
            )}
            <UserMenu />
          </div>
        </div>
      </header>

      <section
        style={{
          background: "linear-gradient(180deg, #143329 0%, #1F4D3D 100%)",
          color: "#fff",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-5">
          <p
            className="text-[11px] uppercase tracking-[0.2em]"
            style={{ color: "rgba(255,255,255,0.7)" }}
          >
            Operations
          </p>
          <h1 className="mt-1 text-2xl md:text-3xl font-extrabold tracking-tight">
            {route === "about" ? "About compliance" : "Timesheet Tamer"}
          </h1>
          <p
            className="mt-1 text-sm max-w-2xl"
            style={{ color: "rgba(255,255,255,0.8)" }}
          >
            {route === "about"
              ? "Rules, expectations, and how the dashboard works."
              : "Compliance requires three things: weekly submission on time, month-end submission, and team-lead approval of Rancho-billed hours."}
          </p>
        </div>
      </section>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-6 space-y-5">
        {route === "about" && <AboutPage />}

        {route === "dashboard" && status.kind === "loading" && (
          <div className="rounded-2xl bg-white shadow-card text-center py-16 text-rancho-slate">
            <div className="text-sm font-medium">Loading compliance data...</div>
            <div className="text-xs mt-1">
              First build of the day pulls multi-month audit logs from Beebole. This can take a couple minutes.
            </div>
          </div>
        )}

        {route === "dashboard" && status.kind === "error" && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800 shadow-card">
            <strong className="font-semibold">Couldn't load compliance data.</strong>
            <div className="mt-1">{status.message}</div>
            <button
              onClick={load}
              className="mt-3 text-red-900 underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        )}

        {route === "dashboard" && status.kind === "ready" && (
          <>
            <SectionBoundary label="KPIs"><KpiCards summary={status.data} /></SectionBoundary>
            <SectionBoundary label="Three pillars"><ThreePillars summary={status.data} /></SectionBoundary>
            <SectionBoundary label="Departments"><DepartmentTiles summary={status.data} /></SectionBoundary>
            <SectionBoundary label="Monthly chart"><MonthEndChart summary={status.data} /></SectionBoundary>
            <SectionBoundary label="Team compliance"><TeamOrgChart summary={status.data} /></SectionBoundary>
            <SectionBoundary label="Repeat offenders"><RepeatOffenders summary={status.data} /></SectionBoundary>
            <SectionBoundary label="Heatmap"><Heatmap summary={status.data} /></SectionBoundary>
          </>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-5 text-xs text-rancho-slate flex justify-between flex-wrap gap-2">
          <span>
            Submission deadline midnight Pacific Mon · Month-end deadline 11:59pm Pacific last day of month
          </span>
          {route === "dashboard" && status.kind === "ready" && status.data.sources && (
            <span>
              {status.data.sources.beebole_roster_count ?? "?"} people ·{" "}
              {(status.data.sources.audit_rows_count ?? 0).toLocaleString()} audit rows ·{" "}
              {(status.data.sources.audit_months ?? []).length} months
            </span>
          )}
        </div>
      </footer>
    </div>
  );
}

function NavLink({
  href,
  active,
  children,
}: {
  href: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      className={
        "px-3 py-1.5 rounded-full text-sm font-semibold transition-colors " +
        (active
          ? "bg-rancho-orange text-white"
          : "text-slate-700 hover:text-rancho-ink hover:bg-slate-100")
      }
    >
      {children}
    </a>
  );
}

function UserMenu() {
  const name = userDisplayName();
  const email = userEmail();
  return (
    <div className="flex items-center gap-2">
      <div className="hidden md:flex flex-col leading-tight text-right">
        <span className="text-xs font-semibold" style={{ color: "#111827" }}>{name}</span>
        {email && <span className="text-[10px]" style={{ color: "#475569" }}>{email}</span>}
      </div>
      <button
        type="button"
        onClick={signOut}
        className="text-xs font-semibold rounded-pill border border-slate-300 px-3 py-1.5 hover:bg-slate-50"
        style={{ color: "#374151", backgroundColor: "#FFFFFF" }}
        title="Sign out of Keycloak"
      >
        Sign out
      </button>
    </div>
  );
}
